/* eslint-disable max-len */
import { isEmpty } from 'lodash/fp';
import { useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useGetApplicationQuery } from '../../../api/queries/getApplication';
import { ApplicationStage } from '../../../api/types';
import { ApplicationDetailsExtraContext } from '../../../components/contexts/ApplicationDetailsExtraContext';
import LoadingElement from '../../../components/LoadingElement';
import { useThemeComponents } from '../../../themes';
import { getApplicationPageElement } from '../../portal/ApplicationListEndpoint/ApplicationDetailsPage';
import NotFoundPage from '../../portal/NotFoundPage';
import {
    eventAdminPath,
    finderAdminPath,
    ReferenceApplicationProvider,
} from '../../shared/ApplicationDetailsPage/standard/ApplicationTab/ReferenceApplicationField';

const ApplicationDetailsPage = () => {
    const { PageWithHeader } = useThemeComponents();
    const { id } = useParams<{ id: string }>();

    const { data, loading, refetch } = useGetApplicationQuery({ variables: { id }, fetchPolicy: 'cache-and-network' });

    const application = data?.application;

    const [applicationStage, applicationStageTitle, stageId] = useMemo(() => {
        const stage = application?.stages.includes(ApplicationStage.Financing)
            ? ApplicationStage.Financing
            : application?.stages[0];

        switch (stage) {
            case ApplicationStage.Financing:
                return [stage, 'application', application?.financingStage?.identifier];

            case ApplicationStage.Reservation:
                return [stage, 'reservation', application?.reservationStage?.identifier];

            case ApplicationStage.Lead:
                return [stage, 'lead', application?.leadStage?.identifier];

            case ApplicationStage.Appointment:
                return [stage, 'appointment', application?.appointmentStage?.identifier];

            case ApplicationStage.Insurance:
                return [stage, 'insurance', application?.insuranceStage?.identifier];

            default:
                return [null, null, null];
        }
    }, [
        application?.appointmentStage?.identifier,
        application?.financingStage?.identifier,
        application?.insuranceStage?.identifier,
        application?.leadStage?.identifier,
        application?.reservationStage?.identifier,
        application?.stages,
    ]);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const extra = useMemo(() => ({ application, refetch }), [application, refetch]);

    if (loading) {
        return <LoadingElement />;
    }

    if (isEmpty(application) || isEmpty(applicationStage)) {
        return <NotFoundPage />;
    }

    return (
        <ApplicationDetailsExtraContext.Provider value={extra}>
            <PageWithHeader
                onBack={() => navigate(-1)}
                title={t(`applicationDetails:titles.${applicationStageTitle}`, { id: stageId })}
            >
                <ReferenceApplicationProvider
                    referencePaths={{
                        stages: {
                            [ApplicationStage.Appointment]: '/admin/appointments',
                            [ApplicationStage.Financing]: '/admin/applications',
                            [ApplicationStage.Insurance]: '/admin/insurances',
                            [ApplicationStage.Lead]: '/admin/leads',
                            [ApplicationStage.Reservation]: '/admin/reservations',
                        },
                        references: {
                            getEventDetail: eventAdminPath,
                            getFinderDetail: finderAdminPath,
                        },
                    }}
                >
                    {getApplicationPageElement(application, applicationStage)}
                </ReferenceApplicationProvider>
            </PageWithHeader>
        </ApplicationDetailsExtraContext.Provider>
    );
};

export default memo(ApplicationDetailsPage);
